import { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useIsPresent, useReducedMotion } from 'framer-motion';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import './BottomSheet.css';

const BACKDROP_TRANSITION = {
  duration: 0.42,
  ease: [0.22, 1, 0.36, 1],
};

const PANEL_TRANSITION = {
  type: 'spring',
  stiffness: 460,
  damping: 38,
  mass: 0.9,
  // Used as the native-animation fallback on older embedded WebViews.
  duration: 0.35,
};
const PANEL_HIDDEN = { transform: 'translate3d(0, 100%, 0)' };
const PANEL_VISIBLE = { transform: 'translate3d(0, 0%, 0)' };

function BottomSheet({
  children,
  className,
  panelClassName,
  isDismissible = true,
  onOpenComplete,
  title,
  titleId,
  onClose,
}) {
  const { common } = useContext(TranslationContext);
  const isPresent = useIsPresent();
  const shouldReduceMotion = useReducedMotion();
  const [hasEntered, setHasEntered] = useState(false);
  const rootClassName = ['bottom-sheet', className].filter(Boolean).join(' ');
  const sheetPanelClassName = ['bottom-sheet__panel', panelClassName].filter(Boolean).join(' ');
  const headingId = titleId || 'bottom-sheet-title';

  useEffect(() => {
    if (!shouldReduceMotion || hasEntered) return;

    setHasEntered(true);
    onOpenComplete?.();
  }, [hasEntered, onOpenComplete, shouldReduceMotion]);

  const sheet = (
    <div
      className={rootClassName}
      role="presentation"
      style={{ pointerEvents: isPresent ? 'auto' : 'none' }}
    >
      <motion.button
        type="button"
        className="bottom-sheet__backdrop"
        aria-label={isDismissible ? common.close : undefined}
        aria-hidden={!isDismissible}
        disabled={!isDismissible}
        onClick={isDismissible ? onClose : undefined}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={BACKDROP_TRANSITION}
      />
      <motion.div
        className={
          hasEntered && isPresent
            ? 'bottom-sheet__panel-shell'
            : 'bottom-sheet__panel-shell bottom-sheet__panel-shell--animating'
        }
        initial={shouldReduceMotion ? false : PANEL_HIDDEN}
        animate={PANEL_VISIBLE}
        exit={shouldReduceMotion ? PANEL_VISIBLE : PANEL_HIDDEN}
        transition={shouldReduceMotion ? { duration: 0 } : PANEL_TRANSITION}
        onAnimationComplete={() => {
          if (isPresent && !hasEntered) {
            setHasEntered(true);
            onOpenComplete?.();
          }
        }}
      >
        <div
          className={sheetPanelClassName}
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
        >
          <div className="bottom-sheet__handle" aria-hidden="true" />
          {title ? (
            <h2 id={headingId} className="bottom-sheet__title">
              {title}
            </h2>
          ) : null}
          {children}
        </div>
      </motion.div>
    </div>
  );

  if (typeof document === 'undefined') {
    return sheet;
  }

  return createPortal(sheet, document.body);
}

export default BottomSheet;
